import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Edit2, Send, Plus, Clock, AlertCircle, Trash2, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Labor-only disclaimer text
const LABOR_ONLY_DISCLAIMER = "This is a labor-only estimate. Materials are not included and will be quoted separately by the contractor.";
const LABOR_ONLY_SHORT = "Labor-only estimate — materials to be quoted separately.";
const FLOORING_DISCLAIMER = "Labor-only estimate. Materials, floor leveling, trim work, and disposal are not included unless specified.";

// Flooring type labels
const FLOORING_TYPE_LABELS = {
  laminate: "Laminate",
  luxury_vinyl: "Luxury Vinyl",
  vinyl_linoleum: "Vinyl/Linoleum",
  wood: "Wood",
  carpet: "Carpet",
  ceramic_tile: "Ceramic/Porcelain Tile",
  natural_stone: "Natural Stone"
};

// Room labels for flooring
const FLOORING_ROOM_LABELS = {
  entire_house: "Entire house",
  all_bedrooms: "All bedrooms",
  bedroom_1: "Bedroom 1",
  bedroom_2: "Bedroom 2",
  more_bedrooms: "Additional bedrooms",
  living_room: "Living room",
  dining_room: "Dining room",
  kitchen: "Kitchen",
  hallways: "Hallways",
  bathrooms: "Bathrooms",
  master_bathroom: "Master bathroom",
  guest_bathroom: "Guest bathroom",
  additional_bathrooms: "Additional bathrooms"
};

// Format price
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);
};

export default function QuoteSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  
  // Get quote data from navigation state
  const quoteData = location.state?.quoteData;
  
  const [showAdditionalServicesPopup, setShowAdditionalServicesPopup] = useState(false);
  const [showTimeframePopup, setShowTimeframePopup] = useState(false);
  const [additionalService, setAdditionalService] = useState("none");
  const [timeframe, setTimeframe] = useState("asap");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (!quoteData) {
      toast.error("No quote data found. Please start a new request.");
      navigate("/");
    }
  }, [quoteData, navigate]);
  
  if (!quoteData) {
    return null;
  }
  
  const { category, answers, estimatedPrice, lineItems, categoryName, zipCode } = quoteData;
  
  // Generate line items from answers
  const generateLineItems = () => {
    const items = [];
    
    // Check if this is a flooring category
    const isFlooring = category === 'flooring' || categoryName?.toLowerCase().includes('flooring');
    
    // Add main category
    items.push({
      name: categoryName || category,
      description: generateDescription(answers, isFlooring),
      price: estimatedPrice,
      hasPricing: estimatedPrice && estimatedPrice.low > 0,
      isFlooring: isFlooring
    });
    
    return items;
  };
  
  // Generate description from answers
  const generateDescription = (answers, isFlooring = false) => {
    const parts = [];
    
    // Painting
    if (answers.painting_type) {
      parts.push(answers.painting_type === 'interior' ? 'Interior' : 'Exterior');
    }
    if (answers.room_types && Array.isArray(answers.room_types)) {
      parts.push(answers.room_types.join(', '));
    }
    
    // Flooring - New room-based format
    if (isFlooring || answers.flooring_rooms) {
      // Add flooring type
      if (answers.flooring_type && FLOORING_TYPE_LABELS[answers.flooring_type]) {
        parts.push(`${FLOORING_TYPE_LABELS[answers.flooring_type]} Flooring`);
      }
      
      // Add rooms as combined description
      if (answers.flooring_rooms && Array.isArray(answers.flooring_rooms)) {
        const roomLabels = [];
        answers.flooring_rooms.forEach(room => {
          if (room === 'bathrooms' && answers.flooring_bathrooms && Array.isArray(answers.flooring_bathrooms)) {
            // Use specific bathroom selections
            answers.flooring_bathrooms.forEach(bathroom => {
              if (FLOORING_ROOM_LABELS[bathroom]) {
                roomLabels.push(FLOORING_ROOM_LABELS[bathroom]);
              }
            });
          } else if (FLOORING_ROOM_LABELS[room]) {
            roomLabels.push(FLOORING_ROOM_LABELS[room]);
          }
        });
        if (roomLabels.length > 0) {
          parts.push(roomLabels.join(', '));
        }
      }
      
      // Add removal indicator (now a radio button with value 'yes' or 'no')
      if (answers.flooring_removal === 'yes') {
        parts.push('+ removal');
      }
    }
    // Legacy flooring sqft support
    else if (answers.flooring_sqft) {
      parts.push(`${answers.flooring_sqft} sq ft`);
      if (answers.flooring_type) {
        parts.push(answers.flooring_type);
      }
    }
    
    // Countertops
    if (answers.countertops_sqft) {
      parts.push(`${answers.countertops_sqft} sq ft`);
    }
    if (answers.countertops_material) {
      parts.push(answers.countertops_material);
    }
    
    // Handyman
    if (answers.handyman_services && Array.isArray(answers.handyman_services)) {
      parts.push(answers.handyman_services.map(s => s.replace(/_/g, ' ')).join(', '));
    }
    
    // Landscaping
    if (answers.lot_size) {
      const unit = answers.lot_size.unit === 'acres' ? 'acres' : 'sq ft';
      parts.push(`Lot: ${answers.lot_size.value} ${unit}`);
    }
    if (answers.landscaping_type && Array.isArray(answers.landscaping_type)) {
      parts.push(answers.landscaping_type.map(s => s.replace(/_/g, ' ')).join(', '));
    }
    
    // Remodel
    if (answers.remodel_type && Array.isArray(answers.remodel_type)) {
      parts.push(answers.remodel_type.map(s => s.replace(/_/g, ' ')).join(', '));
    }
    
    return parts.length > 0 ? parts.join(' — ') : 'See details';
  };
  
  const items = generateLineItems();
  
  // Calculate total
  const totalLow = estimatedPrice?.low || 0;
  const totalHigh = estimatedPrice?.high || 0;
  const hasValidTotal = totalLow > 0 || totalHigh > 0;
  
  // Handle edit request
  const handleEditRequest = () => {
    navigate(`/request/${category}`, { 
      state: { 
        preservedAnswers: answers,
        preservedZipCode: zipCode
      } 
    });
  };
  
  // Handle submit as lead - show first popup
  const handleSubmitAsLead = () => {
    setShowAdditionalServicesPopup(true);
  };
  
  // Handle additional services selection
  const handleAdditionalServicesContinue = () => {
    setShowAdditionalServicesPopup(false);
    setShowTimeframePopup(true);
  };
  
  // Handle final submission
  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Build lead data
      const leadData = {
        category_id: category,
        category_name: categoryName || category,
        service_types: [categoryName || category],
        location_zip: zipCode,
        location_city: "Austin",
        urgency: timeframe === 'asap' ? 'ASAP' : timeframe === '1_2_weeks' ? '1-2 weeks' : 'In a month',
        estimated_min: totalLow,
        estimated_max: totalHigh,
        max_contractors: 3,
        homeowner_id: user?.user_id || `guest_${Date.now()}`,
        homeowner_name: user?.name || "Guest User",
        homeowner_email: user?.email || "",
        homeowner_phone: "",
        homeowner_address: zipCode,
        job_details: generateDescription(answers, category === 'flooring' || categoryName?.toLowerCase().includes('flooring')),
        answers: {
          ...answers,
          additional_service: additionalService,
          is_labor_only: true,
          converted_from_quote: true
        }
      };
      
      // Add additional service as line item if selected
      if (additionalService !== 'none') {
        leadData.answers.additional_services = [additionalService];
      }
      
      // If user is not logged in, create a pending quote and redirect to auth
      if (!user) {
        try {
          // Create a pending quote (not a lead yet)
          const quoteResponse = await axios.post(`${API}/quotes/pending`, {
            category_id: category,
            category_name: categoryName,
            answers: {
              ...answers,
              additional_service: additionalService,
              timeline: timeframe,
              is_labor_only: true
            },
            estimated_min: totalLow,
            estimated_max: totalHigh,
            zip_code: zipCode,
            photo_session_id: quoteData.photo_session_id || null,
            urgency: timeframe === 'asap' ? 'ASAP' : timeframe === '1_2_weeks' ? '1-2 weeks' : 'In a month'
          });
          
          setShowTimeframePopup(false);
          
          // Redirect to post-quote auth page
          navigate('/quote-submitted', {
            state: {
              quoteData: {
                categoryName: categoryName,
                estimatedPrice: { low: totalLow, high: totalHigh },
                zipCode: zipCode
              },
              pendingQuoteId: quoteResponse.data.quote_id
            }
          });
        } catch (error) {
          toast.error("Failed to submit quote. Please try again.");
        } finally {
          setIsSubmitting(false);
        }
        return;
      }
      
      // Create lead for logged-in user
      const response = await axios.post(`${API}/leads`, leadData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setShowTimeframePopup(false);
      toast.success("Your request has been sent to contractors!");
      
      navigate('/dashboard', { 
        state: { tab: 'requests' } 
      });
      
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Save quote for logged-in users
  const handleSaveQuote = async () => {
    if (!user) {
      toast.error("Please log in to save quotes.");
      return;
    }
    
    try {
      await axios.post(`${API}/quotes`, {
        category_id: category,
        category_name: categoryName,
        answers: answers,
        estimated_min: totalLow,
        estimated_max: totalHigh,
        zip_code: zipCode,
        photo_session_id: quoteData.photo_session_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Quote saved to your dashboard!");
      navigate('/dashboard', { state: { tab: 'quotes' } });
    } catch (error) {
      toast.error("Failed to save quote.");
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="quote-summary-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#1F2933]/70"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          {user && (
            <Button
              variant="outline"
              onClick={handleSaveQuote}
              className="text-[#0FA3B1] border-[#0FA3B1]"
            >
              Save Quote
            </Button>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="font-manrope font-bold text-3xl text-[#1F2933] mb-3">
            Your Project Estimate
          </h1>
          <p className="text-[#1F2933]/60 max-w-2xl mx-auto">
            This is a labor-only estimate based on the details you provided. Materials are not included. No contractors have been notified yet.
          </p>
        </div>
        
        {/* Labor-Only Notice - Use flooring-specific disclaimer for flooring category */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-amber-800 text-sm">
            {category === 'flooring' || categoryName?.toLowerCase().includes('flooring') 
              ? FLOORING_DISCLAIMER 
              : LABOR_ONLY_DISCLAIMER}
          </p>
        </div>
        
        {/* Project Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="font-semibold text-[#1F2933]">Project Breakdown</h2>
            <p className="text-sm text-[#1F2933]/60">
              Review each item below. You can make changes before deciding to send this request to contractors.
            </p>
          </div>
          
          <div className="divide-y divide-gray-100">
            {items.map((item, index) => (
              <div key={`${item.name}-${index}`} className="px-6 py-4 flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-[#1F2933]">{item.name}</h3>
                  <p className="text-sm text-[#1F2933]/60 mt-1">{item.description}</p>
                  {item.isFlooring && (
                    <p className="text-xs text-amber-600 mt-1">
                      Materials, floor leveling, trim work, and disposal not included
                    </p>
                  )}
                </div>
                <div className="text-right ml-4">
                  {item.hasPricing ? (
                    <span className="font-semibold text-[#1F2933]">
                      {formatPrice(item.price.low)} – {formatPrice(item.price.high)}
                    </span>
                  ) : (
                    <span className="text-sm text-amber-600 italic">
                      Estimate pending — contractor will provide pricing
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Total Section */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="font-semibold text-[#1F2933]">Estimated Labor Total</span>
            {hasValidTotal ? (
              <span className="font-bold text-2xl text-[#0FA3B1]">
                {formatPrice(totalLow)} – {formatPrice(totalHigh)}
              </span>
            ) : (
              <span className="text-amber-600 italic">
                Pending contractor pricing
              </span>
            )}
          </div>
          <p className="text-xs text-[#1F2933]/50">
            Final labor pricing may vary after an in-person evaluation. Materials will be quoted separately by the contractor.
          </p>
        </div>
        
        {/* Unlisted Service Description (if provided) */}
        {answers?.unlisted_service_description && (
          <div className="bg-[#0FA3B1]/10 rounded-2xl border border-[#0FA3B1]/30 p-6 mb-6" data-testid="unlisted-desc-summary">
            <h3 className="font-semibold text-[#0FA3B1] mb-2">Custom Request</h3>
            <p className="text-[#1F2933] text-sm whitespace-pre-wrap">
              {answers.unlisted_service_description}
            </p>
          </div>
        )}

        {/* Additional Details (if provided) */}
        {answers?.additional_details && (
          <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6 mb-6" data-testid="additional-details-summary">
            <h3 className="font-semibold text-blue-800 mb-2">Additional Details</h3>
            <p className="text-blue-700 text-sm whitespace-pre-wrap">
              {answers.additional_details}
            </p>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            onClick={handleEditRequest}
            className="flex-1 py-6 text-[#1F2933] border-gray-300"
            data-testid="edit-request-btn"
          >
            <Edit2 className="w-5 h-5 mr-2" />
            Edit Request
            <span className="block text-xs text-[#1F2933]/50 ml-2">Make changes to your request</span>
          </Button>
          
          <Button
            onClick={handleSubmitAsLead}
            className="flex-1 py-6 bg-[#0FA3B1] hover:bg-[#0D8C98] text-white"
            data-testid="submit-as-lead-btn"
          >
            <Send className="w-5 h-5 mr-2" />
            Submit as a Lead
            <span className="block text-xs text-white/70 ml-2">Send to available contractors</span>
          </Button>
        </div>
        
        {/* Confirmation Text */}
        <p className="text-center text-sm text-[#1F2933]/50 mt-4">
          This request will be sent to contractors as a labor-only estimate. Contractors will provide material pricing separately if needed.
        </p>
      </main>
      
      {/* Popup 1: Additional Services */}
      <Dialog open={showAdditionalServicesPopup} onOpenChange={setShowAdditionalServicesPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Need any additional services while we&apos;re there?</DialogTitle>
            <DialogDescription>
              Optional — select if you&apos;d like to add another service to this request.
            </DialogDescription>
          </DialogHeader>
          
          <RadioGroup value={additionalService} onValueChange={setAdditionalService} className="mt-4">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="none" />
              <span>No additional services needed</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="fixtures_hardware" />
              <span>Fixtures & Hardware Installation</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="electrical_bath_fixtures" />
              <span>Electrical / Bath Fixtures</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="flooring_lvp" />
              <span>Flooring (LVP)</span>
            </label>
          </RadioGroup>
          
          <Button 
            onClick={handleAdditionalServicesContinue}
            className="w-full mt-4 bg-[#0FA3B1] hover:bg-[#0D8C98]"
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Popup 2: Time Frame */}
      <Dialog open={showTimeframePopup} onOpenChange={setShowTimeframePopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>When do you need this done?</DialogTitle>
            <DialogDescription>
              Select your preferred timeline for this project.
            </DialogDescription>
          </DialogHeader>
          
          <RadioGroup value={timeframe} onValueChange={setTimeframe} className="mt-4">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="asap" />
              <div>
                <span className="font-medium">ASAP</span>
                <p className="text-sm text-gray-500">As soon as possible</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="1_2_weeks" />
              <div>
                <span className="font-medium">1–2 weeks</span>
                <p className="text-sm text-gray-500">Within the next couple weeks</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
              <RadioGroupItem value="in_a_month" />
              <div>
                <span className="font-medium">In a month</span>
                <p className="text-sm text-gray-500">Flexible timing</p>
              </div>
            </label>
          </RadioGroup>
          
          <Button 
            onClick={handleFinalSubmit}
            disabled={isSubmitting}
            className="w-full mt-4 bg-[#0FA3B1] hover:bg-[#0D8C98]"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="spinner w-4 h-4 border-2 border-white/20 border-t-white"></div>
                Submitting...
              </span>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
